// src/app/api/authApi.ts
import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

export type UserRole = "user" | "admin";

export interface AuthUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  role: UserRole;
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user: AuthUser;
  token: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
}

interface MessageResponse {
  success: boolean;
  message: string;
  data: null;
}

interface UserResponse {
  success: boolean;
  message: string;
  data: AuthUser;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),

    logout: builder.mutation<MessageResponse, void>({
      query: () => ({ url: "/auth/logout", method: "POST" }),
    }),

    getCurrentUser: builder.query<UserResponse, void>({
      query: () => "/auth/me",
      providesTags: [{ type: "User", id: "CURRENT" }],
    }),

    updateProfile: builder.mutation<
      UserResponse,
      Partial<Pick<AuthUser, "firstName" | "lastName" | "avatar">>
    >({
      query: (data) => ({
        url: "/auth/profile",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "User", id: "CURRENT" }],
    }),

    changePassword: builder.mutation<
      MessageResponse,
      { currentPassword: string; newPassword: string }
    >({
      query: (data) => ({
        url: "/auth/change-password",
        method: "POST",
        body: data,
      }),
    }),

    // Admin-only: provision another admin / staff account
    createUser: builder.mutation<UserResponse, CreateUserRequest>({
      query: (data) => ({
        url: "/auth/users",
        method: "POST",
        body: data,
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useCreateUserMutation,
} = authApi;
